import React from 'react';
import { 
  Box, 
  Skeleton, 
  Grid, 
  Card, 
  CardContent, 
  Stack,
  useTheme 
} from '@mui/material';

// Skeleton básico para modals genéricos
export const GenericModalSkeleton: React.FC<{ height?: number }> = ({ height = 400 }) => {
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2
      }}
    >
      {/* Header skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '40%' }} />
      </Box>
      
      {/* Content skeleton */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton 
            key={index} 
            variant="text" 
            sx={{ fontSize: '1rem', width: `${Math.random() * 40 + 60}%` }} 
          />
        ))}
        
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 1 }} />
        
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton 
            key={index} 
            variant="text" 
            sx={{ fontSize: '1rem', width: `${Math.random() * 50 + 50}%` }} 
          />
        ))}
      </Box>
    </Box>
  );
};

// Skeleton para modals de drill down de produtos (simples)
export const ProductDrillDownSkeleton: React.FC = () => {
  const theme = useTheme();
  
  return (
    <Grid container spacing={3}>
      {/* Product Info Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '60%', mb: 1 }} />
                <Skeleton variant="text" sx={{ fontSize: '1rem', width: '40%' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      {/* Charts Section */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '40%', mb: 2 }} />
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '50%', mb: 2 }} />
            <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
          </CardContent>
        </Card>
      </Grid>
      
      {/* Tables Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '30%', mb: 2 }} />
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
              {/* Table header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                p: 1, 
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.action.hover
              }}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} variant="text" sx={{ width: '20%', height: '16px' }} />
                ))}
              </Box>
              
              {/* Table rows */}
              {Array.from({ length: 5 }).map((_, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    p: 1,
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                    borderBottom: index < 4 ? `1px solid ${theme.palette.divider}` : 'none'
                  }}
                >
                  {Array.from({ length: 4 }).map((_, colIndex) => (
                    <Skeleton key={colIndex} variant="text" sx={{ width: '20%', height: '14px' }} />
                  ))}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Skeleton complexo para modals de análise detalhada (ProdutoVendasDrillDownModal)
export const DetailedAnalysisSkeleton: React.FC = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ p: 0 }}>
      {/* Header com informações principais */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)` }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={56} height={56} />
                <Box>
                  <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '300px', mb: 1 }} />
                  <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '150px' }} />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Skeleton variant="rounded" width="100%" height={32} />
                <Skeleton variant="rounded" width="100%" height={32} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cards de métricas principais */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid key={index} item xs={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '80%', mx: 'auto', mb: 1 }} />
                <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '60%', mx: 'auto' }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Gráficos principais */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Evolução de Vendas */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '200px' }} />
              </Stack>
              <Box sx={{ height: 320 }}>
                {/* Simular um gráfico de linha */}
                <Box sx={{ position: 'relative', height: '100%' }}>
                  {/* Eixo Y */}
                  <Box sx={{ position: 'absolute', left: 0, top: 20, bottom: 60, width: 60, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton key={index} variant="text" sx={{ width: '50px', height: '10px' }} />
                    ))}
                  </Box>
                  
                  {/* Área do gráfico */}
                  <Box sx={{ ml: 8, mr: 2, height: 'calc(100% - 80px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ position: 'relative', width: '100%', height: '60%' }}>
                      <Skeleton variant="rectangular" sx={{ width: '100%', height: '2px', borderRadius: 1 }} />
                      {/* Pontos da linha */}
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton 
                          key={index}
                          variant="circular" 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            position: 'absolute',
                            left: `${index * 16 + 5}%`,
                            top: `${Math.random() * 60 + 20}%`
                          }} 
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {/* Eixo X */}
                  <Box sx={{ position: 'absolute', bottom: 20, left: 60, right: 20, height: 30, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Skeleton key={index} variant="text" sx={{ width: '40px', height: '10px' }} />
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top CFOPs */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '120px' }} />
              </Stack>
              <Box sx={{ height: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Skeleton variant="circular" width={200} height={200} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Análise de Clientes e Regiões */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Top Clientes */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '150px' }} />
              </Stack>
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, height: 300, overflow: 'hidden' }}>
                {/* Table header */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  p: 1, 
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.action.hover
                }}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton key={index} variant="text" sx={{ width: '20%', height: '16px' }} />
                  ))}
                </Box>
                
                {/* Table rows */}
                {Array.from({ length: 8 }).map((_, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      p: 1,
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                      borderBottom: index < 7 ? `1px solid ${theme.palette.divider}` : 'none'
                    }}
                  >
                    <Box sx={{ width: '40%' }}>
                      <Skeleton variant="text" sx={{ width: '90%', height: '14px', mb: 0.5 }} />
                      <Skeleton variant="text" sx={{ width: '60%', height: '12px' }} />
                    </Box>
                    {Array.from({ length: 3 }).map((_, colIndex) => (
                      <Skeleton key={colIndex} variant="text" sx={{ width: '15%', height: '14px' }} />
                    ))}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendas por Região */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '180px' }} />
              </Stack>
              <Box sx={{ height: 300 }}>
                {/* Simular gráfico de barras */}
                <Box sx={{ position: 'relative', height: '100%' }}>
                  {/* Eixo Y */}
                  <Box sx={{ position: 'absolute', left: 0, top: 20, bottom: 50, width: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton key={index} variant="text" sx={{ width: '70px', height: '10px' }} />
                    ))}
                  </Box>
                  
                  {/* Barras */}
                  <Box sx={{ ml: 10, mr: 2, height: 'calc(100% - 70px)', display: 'flex', alignItems: 'end', justifyContent: 'space-around', gap: 1 }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton 
                        key={index}
                        variant="rectangular" 
                        sx={{ 
                          width: `${100 / 5 - 2}%`,
                          height: `${Math.random() * 60 + 30}%`, 
                          borderRadius: 0.5
                        }} 
                      />
                    ))}
                  </Box>
                  
                  {/* Eixo X */}
                  <Box sx={{ position: 'absolute', bottom: 20, left: 80, right: 20, height: 30, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton key={index} variant="text" sx={{ width: '30px', height: '10px' }} />
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transações Detalhadas */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" sx={{ fontSize: '1.25rem', width: '200px' }} />
          </Stack>
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, height: 400, overflow: 'hidden' }}>
            {/* Table header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              p: 1, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover
            }}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} variant="text" sx={{ width: '12%', height: '16px' }} />
              ))}
            </Box>
            
            {/* Table rows */}
            {Array.from({ length: 10 }).map((_, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  p: 1,
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                  borderBottom: index < 9 ? `1px solid ${theme.palette.divider}` : 'none'
                }}
              >
                {Array.from({ length: 8 }).map((_, colIndex) => (
                  <Skeleton key={colIndex} variant="text" sx={{ width: '12%', height: '14px' }} />
                ))}
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

